import axios, {AxiosResponse} from 'axios';
import Cookies from 'js-cookie';
import {getCenterOf, localStorageGet, postMessage} from 'Util/Utilities';
import {resolveApiPath, isAbsoluteExternalUrl} from 'Util/resolveApiPath';
import {MSG_SET_LOADING_CONT_STYLE, MSG_SET_LOADING_STYLE, MSG_SHOW_SNACKBAR, SHOW_LOADING, UNDEF} from './Constants';

export const ACCESS_TOKEN = 'access_token';
export const ACCESS_TOKEN_EXPIRY = 'access_token_expiry';
export const APPLICATION_REQUEST_TIMEOUT_MS = 15_000;
export const EXTERNAL_UPLOAD_REQUEST_TIMEOUT_MS = 60_000;

export type RequestHeaders = Record<string, string | number | boolean | undefined>;

export type RequestOptions = {
  headers?: RequestHeaders;
  signal?: AbortSignal;
};

export function getAccessToken(): string | undefined {
  const cookieToken = Cookies.get(ACCESS_TOKEN);
  if (cookieToken) return cookieToken;

  const lsToken = localStorageGet(ACCESS_TOKEN);
  if (!lsToken) return undefined;

  const expiry = localStorageGet(ACCESS_TOKEN_EXPIRY);
  if (expiry && Date.now() >= expiry * 1000) return undefined;

  return lsToken;
}

let resolvedApiUrl: string | null = null;
let probePromise: Promise<string> | null = null;
let hasStartedUnauthorizedRecovery = false;

async function probeAndGetBaseUrl(): Promise<string> {
  if (resolvedApiUrl !== null) return resolvedApiUrl;
  if (probePromise !== null) return probePromise;

  probePromise = (async () => {
    const primary = process.env.REACT_APP_API_URL || '';
    const fallbacks = (process.env.REACT_APP_API_URL_FALLBACK || '')
      .split(',')
      .map(u => u.trim())
      .filter(Boolean);

    if (fallbacks.length === 0) {
      resolvedApiUrl = primary;
      return primary;
    }

    for (const url of [primary, ...fallbacks].filter(Boolean)) {
      try {
        await axios.get(`${url}/_/ping`, {timeout: parseInt(process.env.REACT_APP_API_PING_TIMEOUT_MS || '') || 2000, withCredentials: false});
        resolvedApiUrl = url;
        return url;
      } catch (e: any) {
        // Any HTTP response means the domain is reachable — only skip on network-level failure
        if (e.response) {
          resolvedApiUrl = url;
          return url;
        }
      }
    }

    postMessage(MSG_SHOW_SNACKBAR, {
      key: Date.now(),
      variant: 'error',
      message: 'Unable to reach the server. There may be a network issue on your end. Please refresh the page once the issue is resolved.',
      duration: 10000,
    });
    resolvedApiUrl = primary;
    return primary;
  })();

  return probePromise;
}

function authHeader(): Record<string, string> {
  const token = getAccessToken();
  return token ? {Authorization: `Bearer ${token}`} : {};
}

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH';

async function request<T = any>(method: RequestMethod, url: string, data?: unknown, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
  const isExternalTarget = isAbsoluteExternalUrl(url);
  const baseUrl = isExternalTarget ? '' : await probeAndGetBaseUrl();
  const path = resolveApiPath(baseUrl, url);
  const headers = isExternalTarget
    ? {...(options.headers || {})}
    : {...(options.headers || {}), ...authHeader()};
  const config = {
    headers,
    signal: options.signal,
    timeout: isExternalTarget ? EXTERNAL_UPLOAD_REQUEST_TIMEOUT_MS : APPLICATION_REQUEST_TIMEOUT_MS,
    withCredentials: false,
  };

  if (method === 'GET') return axios.get<T>(path, config);
  if (method === 'POST') return axios.post<T>(path, data, config);
  if (method === 'PUT') return axios.put<T>(path, data, config);
  return axios.patch<T>(path, data, config);
}

export function GET<T = any>(url: string, options?: RequestOptions) {
  return request<T>('GET', url, UNDEF, options);
}

export function GET_PUBLIC<T = any>(baseUrl: string, url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
  return axios.get<T>(resolveApiPath(baseUrl, url), {
    headers: {...(options.headers || {})},
    signal: options.signal,
    timeout: APPLICATION_REQUEST_TIMEOUT_MS,
    withCredentials: false,
  });
}

export function POST<T = any>(url: string, params: unknown, options?: RequestOptions) {
  return request<T>('POST', url, params, options);
}

export function PUT<T = any>(url: string, data: unknown, headers?: RequestHeaders, options?: Omit<RequestOptions, 'headers'>) {
  return request<T>('PUT', url, data, {...options, headers});
}

export function PATCH<T = any>(url: string, data: unknown, headers?: RequestHeaders, options?: Omit<RequestOptions, 'headers'>) {
  return request<T>('PATCH', url, data, {...options, headers});
}

export type ResponseCallback<T = any> = (response?: AxiosResponse<T>, error?: any) => void;

function isCanceledRequest(error: unknown): boolean {
  return axios.isCancel?.(error) || (error as {code?: string} | undefined)?.code === 'ERR_CANCELED';
}

function recoverUnauthorizedSession() {
  if (hasStartedUnauthorizedRecovery) return;

  hasStartedUnauthorizedRecovery = true;
  Cookies.remove('userid');
  Cookies.remove(ACCESS_TOKEN);
  localStorage.removeItem(ACCESS_TOKEN);
  localStorage.removeItem(ACCESS_TOKEN_EXPIRY);
  window.location.reload();
}

export function showLoading(show = true, style: any = null) {
  postMessage(SHOW_LOADING, show ? 1 : -1, 1);
  style !== null && postMessage(MSG_SET_LOADING_CONT_STYLE, style);
  return true;
}

export function showSnackbar(
  msg: string,
  variant: string,
  durationMilli?: number | undefined,
  actionTitle?: string,
  onAction?: any,
) {
  postMessage(MSG_SHOW_SNACKBAR, {
    key: new Date().getTime(),
    variant: variant,
    message: msg,
    duration: durationMilli,
    actionTitle: actionTitle,
    onAction: onAction,
  });
}

export default class Api {
  private static call(
    method: RequestMethod,
    url: string,
    params?: unknown,
    respCallback?: ResponseCallback,
    showLoader: boolean | HTMLElement = true,
    showErrorSnackbar = true,
    showRetry = false,
    headers?: RequestHeaders,
  ) {
    if (showLoader) {
      if (showLoader instanceof HTMLElement) {
        //position loading indicator in center of html element
        const center = getCenterOf(showLoader);
        postMessage(MSG_SET_LOADING_CONT_STYLE, {
          position: 'absolute',
          left: center.x - 30,
          top: center.y - 31,
        });
        postMessage(MSG_SET_LOADING_STYLE, {
          marginLeft: 0,
          marginTop: 0,
        });
      } else {
        postMessage(MSG_SET_LOADING_CONT_STYLE, {});
        postMessage(MSG_SET_LOADING_STYLE, {});
      }
      showLoading();
    }

    const request =
      method === 'GET'
        ? GET(url)
        : method === 'POST'
          ? POST(url, params)
          : method === 'PATCH'
            ? PATCH(url, params, headers)
            : PUT(url, params, headers);
    request.then(
      res => {
        showLoader && showLoading(false);
        respCallback && respCallback(res);

        const data = res && res.data;
        const success = data && data.success;
        const message = data && data.message;

        console.log('API Response:', {
          url,
          raw: res,
          data,
          success,
          message,
        });

        if (success === false && showErrorSnackbar) {
          const actionBtnTxt = showRetry ? 'Retry' : UNDEF;
          const callback = !actionBtnTxt
            ? UNDEF
            : () => {
                Api.call(method, url, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
              };
          showSnackbar(message, 'warning', UNDEF, actionBtnTxt, callback);
        }
      },
      err => {
        showLoader && showLoading(false);
        if (isCanceledRequest(err)) return;

        respCallback && respCallback(UNDEF, err);

        const resp = (err as {response?: {status?: number; data?: unknown}}).response;
        const isAuthProblem = resp?.status === 401;
        if (isAuthProblem) recoverUnauthorizedSession();

        if (showErrorSnackbar) {
          // Don't show network errors (have another snackabr for that)
          if (!resp) return;

          let data: any = resp.data;
          // handle double data
          if (data && data.data) data = data.data;
          const errMsg = data ? data.error_reason || data.error || data.error_message || data.message : err.message;
          const error = errMsg;
          //  + '\n/' + url;

          const actionBtnTxt = !isAuthProblem && showRetry ? 'Retry' : UNDEF;
          const callback = !actionBtnTxt
            ? UNDEF
            : () => {
                Api.call(method, url, params, respCallback, showLoader, showErrorSnackbar, showRetry, headers);
              };
          showSnackbar(error, 'error', UNDEF, actionBtnTxt, callback);
        }
      },
    );
  }

  static GET(
    url: string,
    respCallback?: ResponseCallback,
    showLoading: any = true,
    showErrorSnackbar = true,
    showRetry = false,
  ) {
    Api.call('GET', url, UNDEF, respCallback, showLoading, showErrorSnackbar, showRetry);
  }

  static POST(
    url: string,
    params: unknown,
    respCallback?: ResponseCallback,
    showLoading: any = true,
    showErrorSnackbar = true,
    showRetry = false,
  ) {
    Api.call('POST', url, params, respCallback, showLoading, showErrorSnackbar, showRetry);
  }

  static PUT(
    url: string,
    data: unknown,
    respCallback?: ResponseCallback,
    showLoading: boolean | HTMLElement = true,
    showErrorSnackbar = true,
    showRetry = false,
    headers?: RequestHeaders,
  ) {
    Api.call('PUT', url, data, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
  }

  static PATCH(
    url: string,
    data: unknown,
    respCallback?: ResponseCallback,
    showLoading: boolean | HTMLElement = true,
    showErrorSnackbar = true,
    showRetry = false,
    headers?: RequestHeaders,
  ) {
    Api.call('PATCH', url, data, respCallback, showLoading, showErrorSnackbar, showRetry, headers);
  }
}
